// src/data/resources/articles/features-settings.ts

export const featuresSettingsArticles = [
  {
    id: "smart-categorization",
    categoryId: "features-settings",
    title: "Smart Categorization",
    content: `
      <h2>Smart Categorization</h2>
      <p>One of MailMaid's most powerful features is Smart Categorization, which automatically sorts your emails into intuitive categories to help you focus on what matters most.</p>
      
      <h3>How Smart Categorization Works</h3>
      <p>MailMaid uses advanced AI to analyze various aspects of your emails:</p>
      <ul>
        <li><strong>Sender information</strong> - Domain, contact status, previous interactions</li>
        <li><strong>Email content</strong> - Keywords, sentiment, formatting patterns</li>
        <li><strong>Engagement history</strong> - How you've interacted with similar emails</li>
        <li><strong>Temporal patterns</strong> - Time sent, frequency, regularity</li>
      </ul>
      <p>Based on this analysis, MailMaid assigns each email to the most appropriate category. The system continues to learn from your behavior to improve accuracy over time.</p>
      
      <h3>Default Categories</h3>
      <p>MailMaid comes with several pre-configured categories:</p>
      <ul>
        <li><strong>Priority</strong> - Important emails requiring immediate attention</li>
        <li><strong>Personal</strong> - Messages from friends, family, and personal contacts</li>
        <li><strong>Work</strong> - Business-related communications</li>
        <li><strong>Newsletters</strong> - Regular updates and subscriptions</li>
        <li><strong>Promotions</strong> - Marketing offers and sales</li>
        <li><strong>Social</strong> - Notifications from social networks</li>
        <li><strong>Updates</strong> - Service notifications and account alerts</li>
        <li><strong>Forums</strong> - Discussion group and community emails</li>
      </ul>
      
      <h3>Creating Custom Categories</h3>
      <p>You can create your own categories to match your specific needs:</p>
      <ol>
        <li>Go to "Settings" > "Categories"</li>
        <li>Click "Add New Category"</li>
        <li>Name your category and choose an icon</li>
        <li>Set up rules to define which emails should be included</li>
        <li>Choose whether to apply to existing emails</li>
      </ol>
      <p>Examples of useful custom categories include "Travel", "Receipts", "Family", "Projects", or "Clients".</p>
      
      <h3>Training the AI</h3>
      <p>To improve categorization accuracy:</p>
      <ol>
        <li><strong>Manual recategorization</strong> - Drag and drop emails to the correct category</li>
        <li><strong>Bulk move</strong> - Select multiple emails and move them to train patterns</li>
        <li><strong>Feedback</strong> - Use the "Wrong category?" option on misclassified emails</li>
      </ol>
      <p>The more you interact with MailMaid, the more accurately it will categorize your emails.</p>
      
      <h3>Category Views</h3>
      <p>There are several ways to view your categorized emails:</p>
      <ul>
        <li><strong>Category tabs</strong> - Switch between categories</li>
        <li><strong>Combined view</strong> - See all emails with category indicators</li>
        <li><strong>Priority focus</strong> - Show only important emails</li>
        <li><strong>Custom filters</strong> - Combine categories with other search criteria</li>
      </ul>
      
      <h3>Category Settings</h3>
      <p>Customize how categories behave:</p>
      <ul>
        <li>Set notification preferences per category</li>
        <li>Configure auto-archiving rules</li>
        <li>Adjust display order in the sidebar</li>
        <li>Enable or disable categories</li>
        <li>Set up email forwarding based on categories</li>
      </ul>
      
      <p>Smart Categorization is designed to transform your inbox from a cluttered mess into an organized workspace where you can focus on what matters most. Take some time to customize it to your workflow for the best results.</p>
    `,
    excerpt: "Learn how MailMaid's AI automatically organizes your emails into intuitive categories.",
    order: 1,
    featured: true
  },
  {
    id: "email-cleanup",
    categoryId: "features-settings",
    title: "Email Cleanup Tools",
    content: `
      <h2>Email Cleanup Tools</h2>
      <p>MailMaid offers powerful tools to help you declutter your inbox quickly and efficiently.</p>
      
      <h3>Smart Clean</h3>
      <p>The Smart Clean feature is your go-to solution for rapid inbox decluttering:</p>
      <ol>
        <li>Click the "Smart Clean" button on your dashboard</li>
        <li>MailMaid analyzes your inbox and groups similar emails</li>
        <li>AI suggests actions (archive, delete, or keep) for each group</li>
        <li>Review the suggestions and make any adjustments</li>
        <li>Confirm to process all selected emails at once</li>
      </ol>
      <p>Smart Clean is particularly effective for handling:</p>
      <ul>
        <li>Old newsletters and promotional emails</li>
        <li>Notification emails that no longer need attention</li>
        <li>Resolved conversations</li>
        <li>Outdated information</li>
      </ul>
      
      <h3>Bulk Unsubscribe</h3>
      <p>Manage your subscriptions with ease:</p>
      <ol>
        <li>Go to the "Newsletters" category</li>
        <li>MailMaid identifies all subscription emails by sender</li>
        <li>View stats on how often you receive and open each newsletter</li>
        <li>Select unwanted subscriptions and click "Unsubscribe"</li>
        <li>MailMaid handles the unsubscribe process automatically</li>
      </ol>
      
      <h3>Duplicate Finder</h3>
      <p>Identify and remove duplicate emails:</p>
      <ol>
        <li>Go to "Tools" > "Find Duplicates"</li>
        <li>MailMaid scans your inbox for identical or very similar emails</li>
        <li>Review the found duplicates, grouped by conversation</li>
        <li>Select which versions to keep or delete</li>
        <li>Confirm to remove the duplicates</li>
      </ol>
      
      <h3>Attachment Manager</h3>
      <p>Find and organize emails with attachments:</p>
      <ol>
        <li>Go to "Tools" > "Attachment Manager"</li>
        <li>View all emails with attachments, sorted by size or date</li>
        <li>Filter by attachment type (documents, images, etc.)</li>
        <li>Download, delete, or archive attachments in bulk</li>
        <li>Free up valuable storage space</li>
      </ol>
      
      <h3>Scheduled Cleanup</h3>
      <p>Set up automatic cleaning routines:</p>
      <ol>
        <li>Go to "Settings" > "Scheduled Cleanup"</li>
        <li>Create a new scheduled task</li>
        <li>Define which emails to target (age, category, read status, etc.)</li>
        <li>Choose the action (archive, delete, move)</li>
        <li>Set the frequency (daily, weekly, monthly)</li>
        <li>Enable email notifications for cleanup summaries</li>
      </ol>
      
      <h3>Advanced Search & Clean</h3>
      <p>For targeted cleanup operations:</p>
      <ol>
        <li>Use the advanced search with multiple criteria</li>
        <li>Examples: "older than 6 months" + "category:newsletters" + "unread:no"</li>
        <li>Select all matching results</li>
        <li>Apply bulk actions (archive, delete, categorize)</li>
      </ol>
      
      <p>Regular use of these cleanup tools will help you maintain a manageable inbox and reduce email overload. We recommend running Smart Clean weekly and scheduling deeper cleanups monthly for optimal results.</p>
    `,
    excerpt: "Discover the powerful tools MailMaid offers to declutter and organize your inbox.",
    order: 2,
    featured: true
  },
  {
    id: "ai-responses",
    categoryId: "features-settings",
    title: "AI Response Suggestions",
    content: `
      <h2>AI Response Suggestions</h2>
      <p>MailMaid's AI Response feature helps you reply to emails quickly and effectively by generating contextually appropriate responses based on the email content.</p>
      
      <h3>How AI Responses Work</h3>
      <p>When you open an email, MailMaid analyzes:</p>
      <ul>
        <li>The content and context of the message</li>
        <li>Your relationship with the sender</li>
        <li>Previous conversations in the thread</li>
        <li>Your typical response style</li>
      </ul>
      <p>Based on this analysis, it generates personalized response suggestions that match your writing style and the email context.</p>
      
      <h3>Using AI Responses</h3>
      <ol>
        <li>Open an email that requires a response</li>
        <li>Click the "Suggest Response" button below the email</li>
        <li>MailMaid will offer several response options</li>
        <li>Select the most appropriate suggestion</li>
        <li>Edit the response as needed</li>
        <li>Send your reply</li>
      </ol>
      
      <h3>Response Types</h3>
      <p>MailMaid offers various types of response suggestions:</p>
      <ul>
        <li><strong>Quick Replies</strong> - Short, simple responses (Thank you, Confirmed, etc.)</li>
        <li><strong>Detailed Responses</strong> - Longer, more comprehensive replies</li>
        <li><strong>Clarification Requests</strong> - Questions to gather more information</li>
        <li><strong>Meeting Proposals</strong> - Suggesting times to connect</li>
        <li><strong>Follow-ups</strong> - Checking in on previous conversations</li>
      </ul>
      
      <h3>Training Your AI</h3>
      <p>To improve response quality:</p>
      <ol>
        <li><strong>Edit suggestions</strong> - The AI learns from your edits</li>
        <li><strong>Provide feedback</strong> - Rate suggestions to improve future ones</li>
        <li><strong>Save to templates</strong> - Convert good suggestions to reusable templates</li>
        <li><strong>Personal dictionary</strong> - Add specific terms for your field or company</li>
      </ol>
      
      <h3>Custom Response Styles</h3>
      <p>Adjust how the AI writes for different situations:</p>
      <ol>
        <li>Go to "Settings" > "AI Responses"</li>
        <li>Create different response styles (Formal, Casual, Concise, Detailed)</li>
        <li>Toggle between styles when generating responses</li>
        <li>Set default styles for specific senders or categories</li>
      </ol>
      
      <h3>Privacy Considerations</h3>
      <p>Important points about AI response privacy:</p>
      <ul>
        <li>All email processing happens securely on our servers</li>
        <li>Your data is never used to train general AI models</li>
        <li>Only your personal response patterns inform suggestions</li>
        <li>You can disable AI responses for sensitive email addresses</li>
      </ul>
      
      <h3>Advanced Features</h3>
      <ul>
        <li><strong>Multilingual support</strong> - Generates responses in the same language as the original email</li>
        <li><strong>Smart scheduling</strong> - Suggests meeting times based on your calendar</li>
        <li><strong>Tone detection</strong> - Matches the emotional tone of the original message</li>
        <li><strong>Follow-up reminders</strong> - Suggests when to check back if no response is received</li>
      </ul>
      
      <p>AI Response Suggestions can save you significant time while ensuring your emails remain personalized and appropriate. The more you use this feature, the better it becomes at matching your unique communication style.</p>
    `,
    excerpt: "Learn how MailMaid's AI can help you respond to emails quickly with contextually appropriate suggestions.",
    order: 3
  },
  {
    id: "custom-rules",
    categoryId: "features-settings",
    title: "Custom Rules & Filters",
    content: `
      <h2>Custom Rules & Filters</h2>
      <p>MailMaid's custom rules and filters allow you to automate your email workflow with precise conditions and actions tailored to your needs.</p>
      
      <h3>Creating Basic Rules</h3>
      <ol>
        <li>Go to "Settings" > "Rules & Filters"</li>
        <li>Click "Create New Rule"</li>
        <li>Name your rule (e.g., "Archive Newsletters")</li>
        <li>Set up conditions (from, subject, content, etc.)</li>
        <li>Choose actions (categorize, archive, forward, etc.)</li>
        <li>Save and activate your rule</li>
      </ol>
      
      <h3>Available Conditions</h3>
      <p>Rules can be based on numerous criteria:</p>
      <ul>
        <li><strong>Sender</strong> - Email address, domain, or contact status</li>
        <li><strong>Subject</strong> - Contains, starts with, or matches exactly</li>
        <li><strong>Content</strong> - Specific words or phrases in the body</li>
        <li><strong>Attachments</strong> - Present, type, or size</li>
        <li><strong>Time</strong> - Day of week, time of day, date range</li>
        <li><strong>Priority</strong> - High, normal, or low importance</li>
        <li><strong>Previously</strong> - Previous interactions with similar emails</li>
      </ul>
      
      <h3>Available Actions</h3>
      <p>When conditions are met, MailMaid can:</p>
      <ul>
        <li><strong>Categorize</strong> - Assign to specific categories</li>
        <li><strong>Archive</strong> - Move out of inbox automatically</li>
        <li><strong>Delete</strong> - Remove completely</li>
        <li><strong>Star/Flag</strong> - Mark as important</li>
        <li><strong>Forward</strong> - Send to another email address</li>
        <li><strong>Apply Label</strong> - Add custom labels</li>
        <li><strong>Send Notification</strong> - Alert you via app or SMS</li>
        <li><strong>Auto-Reply</strong> - Send automated responses</li>
      </ul>
      
      <h3>Rule Examples</h3>
      <p>Here are some useful rule examples:</p>
      <ul>
        <li><strong>Receipt Organizer</strong> - If subject contains "receipt" or "invoice", categorize as "Receipts" and archive</li>
        <li><strong>VIP Contacts</strong> - If sender is in "VIP" contact list, categorize as "Priority" and send notification</li>
        <li><strong>Newsletter Manager</strong> - If sender domain matches known newsletter services, categorize as "Newsletters"</li>
        <li><strong>Weekend Quiet</strong> - If received on weekend, categorize as "Weekend" and mute notifications</li>
      </ul>
      
      <h3>Creating Advanced Rules</h3>
      <p>For more complex scenarios:</p>
      <ol>
        <li>Use the "Advanced" tab when creating rules</li>
        <li>Combine multiple conditions with AND/OR logic</li>
        <li>Create condition groups with their own logical operators</li>
        <li>Use regular expressions for pattern matching</li>
        <li>Chain multiple actions in sequence</li>
      </ol>
      
      <h3>Rule Management</h3>
      <p>Keep your rules organized and effective:</p>
      <ul>
        <li><strong>Rule order</strong> - Drag and drop to set priority sequence</li>
        <li><strong>Enable/disable</strong> - Temporarily turn rules on or off</li>
        <li><strong>Rule statistics</strong> - See how often each rule is triggered</li>
        <li><strong>Rule testing</strong> - Test against existing emails before activating</li>
        <li><strong>Importing/exporting</strong> - Share rules between accounts</li>
      </ul>
      
      <h3>Filters vs. Rules</h3>
      <p>In MailMaid:</p>
      <ul>
        <li><strong>Rules</strong> - Automatic actions applied to incoming emails</li>
        <li><strong>Filters</strong> - Ways to view existing emails based on criteria</li>
      </ul>
      <p>You can create saved filters by clicking "Save" after using the advanced search function.</p>
      
      <p>Well-configured rules can dramatically reduce manual email processing, ensuring important messages get attention while routine emails are handled automatically. We recommend starting with a few simple rules and gradually adding more as you identify repetitive email patterns.</p>
    `,
    excerpt: "Create personalized automation rules to handle your emails exactly how you want them.",
    order: 4
  }
];